import { useCallback, useMemo, useState } from 'react';
import {
  OwcTypography,
  OwcCard,
  OwcComponentHeader,
  OwcButton,
  OwcIcon,
  OwcCheckbox,
} from '@one/react';
import styles from './ReleaseHistory.module.css';
import { useGetReleasesQuery, useDownloadSchemaMutation } from '../../rtk/api/datagroupsApi';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/general/formatDate';
import { decodeUserInfo, canDeprecate } from '../../utils/authhelpers/authhelpers';
import { formatApiError } from '../../utils/release/apiError';
import DeprecateReleaseModal from './components/DeprecateRelease/DeprecateReleaseModal';
import HardDeleteReleaseModal from './components/HardDeleteRelease/HardDeleteReleaseModal';
import Banner from '../../components/Common/Banner';
import StatusChip from '../../components/Common/StatusChip';
import { isLatestRelease } from '../../utils/release/deprecationPlan';
import { useGroupData } from '../../contexts/GroupDataContext';

const GROUP_NAME = 'Lab';

export default function ReleaseHistoryPage() {
  const [downloadingVersion, setDownloadingVersion] = useState(null);
  const [showDeprecated, setShowDeprecated] = useState(false);
  const [deprecateTarget, setDeprecateTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Action feedback shown in a dismissible banner: { variant, text } | null.
  const [actionMessage, setActionMessage] = useState(null);
  const dismissActionMessage = useCallback(() => setActionMessage(null), []);
  const navigate = useNavigate();
  const auth = useAuth();
  const { setLatestVersion } = useGroupData();

  const [downloadSchema] = useDownloadSchemaMutation();

  const userInfo = useMemo(
    () =>
      auth.user?.id_token ? decodeUserInfo(auth.user.id_token, auth.user.refresh_token) : null,
    [auth.user?.id_token, auth.user?.refresh_token]
  );
  const allowDeprecate = canDeprecate(userInfo?.groups);

  const {
    data: releasesData,
    error,
    isLoading,
  } = useGetReleasesQuery(
    { groupName: GROUP_NAME, includeDeprecated: showDeprecated },
    { skip: !auth.user?.id_token }
  );

  const releases = releasesData?.releases;

  if (!auth.user?.id_token) return <OwcTypography>Loading auth...</OwcTypography>;
  if (isLoading) return <OwcTypography>Loading releases...</OwcTypography>;
  if (error)
    return (
      <div style={{ padding: '24px' }}>
        <Banner variant="error">{formatApiError(error, 'Failed to load releases.')}</Banner>
      </div>
    );

  const handleSchemaNavigation = (release) => {
    // Deprecated release schemas are gone from S3 (410), so the viewer would
    // render an empty page. Surface the same warning the download path shows
    // instead of navigating.
    if ((release.releaseStatus || 'Active') !== 'Active') {
      setActionMessage({
        variant: 'warning',
        text: `Release ${release.releaseVersion} has been deprecated and its schema is no longer available to view.`,
      });
      return;
    }
    setActionMessage(null);
    navigate(`/lab/releases/${release.releaseVersion}.schema.json`);
  };

  const handleDownload = async (release) => {
    setActionMessage(null);
    setDownloadingVersion(release.releaseVersion);
    const result = await downloadSchema({
      groupName: GROUP_NAME,
      releaseVersion: release.releaseVersion,
    });
    setDownloadingVersion(null);
    if (result?.error) {
      // 410 Gone → release has been deprecated.
      const status = result.error.status;
      setActionMessage(
        status === 410
          ? {
              variant: 'warning',
              text: `Release ${release.releaseVersion} has been deprecated and is no longer available for download.`,
            }
          : { variant: 'error', text: formatApiError(result.error, 'Download failed.') }
      );
    }
  };

  const handleDeprecated = (result) => {
    const affected =
      result?.affectedEntities != null ? ` (${result.affectedEntities} entities affected)` : '';
    setActionMessage({
      variant: 'success',
      // Destructive-action confirmations persist; the user dismisses them so the
      // outcome is not missed.
      persist: true,
      text:
        (result?.message || `Release ${deprecateTarget?.releaseVersion} deprecated successfully`) +
        affected,
    });
    // The latest active release changed; clear the cached latest version so the
    // rest of the app refetches.
    setLatestVersion('');
    // The sidebar keeps its release list in local state fed by a lazy query, so
    // RTK cache invalidation alone won't update it.
    window.dispatchEvent(new CustomEvent('refreshReleases'));
  };

  const handleDeleted = (result) => {
    const affected =
      result?.affectedEntities != null ? ` (${result.affectedEntities} entities affected)` : '';
    setActionMessage({
      variant: 'success',
      // Destructive-action confirmations persist; the user dismisses them so the
      // outcome is not missed.
      persist: true,
      text:
        (result?.message || `Release ${deleteTarget?.releaseVersion} deleted successfully`) +
        affected,
    });
    // The latest release was removed; clear the cached latest version so the
    // rest of the app refetches.
    setLatestVersion('');
    // The sidebar keeps its release list in local state fed by a lazy query, so
    // RTK cache invalidation alone won't update it.
    window.dispatchEvent(new CustomEvent('refreshReleases'));
  };

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <OwcTypography variant="title4">Published Release Versions</OwcTypography>
          <OwcTypography className={styles.block_display}>
            View 'Release Notes' and 'Download' Release Schema Bundle.
          </OwcTypography>
        </div>
        <OwcCheckbox checked={showDeprecated} onValueChange={(e) => setShowDeprecated(e.detail)}>
          <span style={{ fontSize: 13, color: 'var(--one-color-gray-600)' }}>
            Show deprecated releases
          </span>
        </OwcCheckbox>
      </div>

      {actionMessage && (
        <Banner
          // Remount per message so the auto-hide countdown restarts for each one.
          key={`${actionMessage.variant}:${actionMessage.text}`}
          variant={actionMessage.variant}
          onDismiss={dismissActionMessage}
          // Transient feedback (e.g. download 410) auto-dismisses; destructive
          // action outcomes persist until dismissed. Errors never auto-hide
          // (handled in Banner).
          autoHideMs={actionMessage.persist ? undefined : 6000}
        >
          {actionMessage.text}
        </Banner>
      )}

      <div className={styles.releasehistory_card_container}>
        {releases?.map((release) => (
          <OwcCard className={styles.owc_card} key={release.releaseVersion} rounded elevated>
            <OwcComponentHeader
              style={{ background: 'var(--one-color-gray-50)', height: '5rem', padding: '16px' }}
              slot="header"
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <OwcTypography
                  style={{ fontSize: '1.5rem', fontWeight: '500', marginRight: '1rem' }}
                >
                  {release.releaseVersion}
                </OwcTypography>
                {release.releaseDate && (
                  <StatusChip tone="neutral">{formatDate(release.releaseDate)}</StatusChip>
                )}
                {(() => {
                  const isActive = (release.releaseStatus || 'Active') === 'Active';
                  return (
                    <StatusChip
                      tone={isActive ? 'active' : 'deprecated'}
                      style={{ marginLeft: '0.75rem' }}
                    >
                      {isActive ? 'Active' : 'Deprecated'}
                    </StatusChip>
                  );
                })()}
              </div>
              <div
                slot="suffix"
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  minWidth: '50%',
                  gap: '1rem',
                }}
              >
                <OwcButton onClick={() => handleSchemaNavigation(release)} variant="secondary">
                  View
                  <OwcIcon
                    style={{
                      color: 'var(--one-color-chart-blue-500)',
                      paddingLeft: '4px',
                      cursor: 'pointer',
                    }}
                    name="file"
                    family="outlined"
                  ></OwcIcon>
                </OwcButton>
                <OwcButton
                  onClick={() => handleDownload(release)}
                  disabled={downloadingVersion === release.releaseVersion}
                >
                  {downloadingVersion === release.releaseVersion ? 'Downloading...' : 'Download'}
                  <OwcIcon
                    style={{ color: 'white', paddingLeft: '4px', cursor: 'pointer' }}
                    name="download"
                    family="outlined"
                  ></OwcIcon>
                </OwcButton>
                {/* Active releases can be retired. The latest active release is
                    hard-deleted (frees the release#version PK so it can be
                    recreated); older active releases are deprecated. */}
                {allowDeprecate &&
                  (release.releaseStatus || 'Active') === 'Active' &&
                  (isLatestRelease(release, releases) ? (
                    <OwcButton
                      variant="secondary"
                      onClick={() => setDeleteTarget(release)}
                      style={{ '--one-button-text-color': 'var(--one-color-cobas-red-500, #c00)' }}
                    >
                      Delete
                      <OwcIcon
                        style={{
                          color: 'var(--one-color-cobas-red-500, #c00)',
                          paddingLeft: '4px',
                          cursor: 'pointer',
                        }}
                        name="delete_forever"
                        family="outlined"
                      ></OwcIcon>
                    </OwcButton>
                  ) : (
                    <OwcButton
                      variant="secondary"
                      onClick={() => setDeprecateTarget(release)}
                      style={{ '--one-button-text-color': 'var(--one-color-cobas-red-500, #c00)' }}
                    >
                      Deprecate
                      <OwcIcon
                        style={{
                          color: 'var(--one-color-cobas-red-500, #c00)',
                          paddingLeft: '4px',
                          cursor: 'pointer',
                        }}
                        name="delete"
                        family="outlined"
                      ></OwcIcon>
                    </OwcButton>
                  ))}
              </div>
            </OwcComponentHeader>

            <div className={styles.release_notes_content}>
              <div style={{ padding: '1rem' }}>
                <OwcTypography
                  style={{
                    color: 'var(--one-color-gray-400)',
                    paddingBottom: '8px',
                    display: 'block',
                  }}
                >
                  RELEASE NOTES
                </OwcTypography>
                <OwcTypography>
                  {release.releaseNotes || 'No release notes available.'}
                </OwcTypography>
              </div>
            </div>
          </OwcCard>
        ))}
      </div>

      {deprecateTarget && (
        <DeprecateReleaseModal
          open={!!deprecateTarget}
          onClose={() => setDeprecateTarget(null)}
          groupName={GROUP_NAME}
          releaseVersion={deprecateTarget.releaseVersion}
          userEmail={userInfo?.email}
          onDeprecated={handleDeprecated}
        />
      )}

      {deleteTarget && (
        <HardDeleteReleaseModal
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          groupName={GROUP_NAME}
          releaseVersion={deleteTarget.releaseVersion}
          userEmail={userInfo?.email}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
}
